<template>
  <div>
    <!-- Hero Section -->
    <v-parallax
      dark
      src="data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Cg fill-opacity='0.1'%3E%3Cpolygon fill='%23000' points='50 0 60 40 100 50 60 60 50 100 40 60 0 50 40 40'/%3E%3C/g%3E%3C/svg%3E"
      height="600"
      class="hero-section"
    >
      <v-row align="center" justify="center" class="hero-content">
        <v-col cols="12" md="8" class="text-center">
          <v-fade-transition appear>
            <div>
              <v-icon size="80" color="primary" class="mb-4">
                mdi-store
              </v-icon>
              <h1 class="display-2 font-weight-bold mb-4">
                Sistema ERP AI-POS
              </h1>
              <h2 class="subtitle-1 mb-6" style="font-size: 1.5rem;">
                Solución completa de gestión empresarial con inteligencia artificial
              </h2>
              <p class="title font-weight-light mb-8">
                Potencia tu negocio con tecnología de vanguardia, facturación electrónica, 
                gestión multi-tienda y soporte internacional
              </p>
              <v-btn
                x-large
                color="primary"
                class="ma-2"
                @click="scrollToFeatures"
              >
                <v-icon left>mdi-rocket-launch</v-icon>
                Descubrir Características
              </v-btn>
              <v-btn
                x-large
                outlined
                color="white"
                class="ma-2"
                @click="goToDemo"
              >
                <v-icon left>mdi-play-circle</v-icon>
                Ver Demo
              </v-btn>
            </div>
          </v-fade-transition>
        </v-col>
      </v-row>
    </v-parallax>

    <!-- Features Section -->
    <section id="features" class="py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Características Principales
            </h2>
            <p class="title font-weight-light">
              Todo lo que necesitas para gestionar tu negocio en una sola plataforma
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="feature in mainFeatures"
            :key="feature.title"
            cols="12"
            md="4"
            class="mb-4"
          >
            <v-card
              class="feature-card h-100"
              hover
              elevation="4"
              @click="feature.action"
            >
              <v-card-text class="text-center pa-8">
                <v-avatar size="80" :color="feature.color" class="mb-4">
                  <v-icon size="40" dark>{{ feature.icon }}</v-icon>
                </v-avatar>
                <h3 class="headline font-weight-bold mb-3">
                  {{ feature.title }}
                </h3>
                <p class="body-1 font-weight-light">
                  {{ feature.description }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Modules Section -->
    <section class="grey lighten-5 py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Módulos Integrados
            </h2>
            <p class="title font-weight-light">
              Gestión completa de todos los aspectos de tu empresa
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="module in modules"
            :key="module.name"
            cols="12"
            sm="6"
            md="3"
            class="mb-4"
          >
            <v-card class="module-card h-100" hover elevation="2">
              <v-card-text class="text-center pa-6">
                <v-avatar size="60" :color="module.color" class="mb-3">
                  <v-icon dark>{{ module.icon }}</v-icon>
                </v-avatar>
                <h4 class="subtitle-1 font-weight-bold mb-2">
                  {{ module.name }}
                </h4>
                <p class="caption">
                  {{ module.description }}
                </p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Technology Stack -->
    <section class="py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Tecnología de Vanguardia
            </h2>
            <p class="title font-weight-light">
              Construido con las mejores tecnologías del mercado
            </p>
          </v-col>
        </v-row>

        <v-row align="center" justify="center">
          <v-col
            v-for="tech in technologies"
            :key="tech.name"
            cols="12"
            sm="6"
            md="2"
            class="text-center mb-4"
          >
            <v-tooltip top>
              <template v-slot:activator="{ on, attrs }">
                <div
                  v-bind="attrs"
                  v-on="on"
                  class="tech-item"
                >
                  <v-avatar size="80" :color="tech.color" class="mb-2">
                    <v-icon size="40" dark>{{ tech.icon }}</v-icon>
                  </v-avatar>
                  <h4 class="subtitle-2 font-weight-bold">
                    {{ tech.name }}
                  </h4>
                </div>
              </template>
              <span>{{ tech.description }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Benefits Section -->
    <section class="primary white--text py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              ¿Por Qué Elegir AI-POS?
            </h2>
            <p class="title font-weight-light">
              Ventajas competitivas que transformarán tu negocio
            </p>
          </v-col>
        </v-row>

        <v-row>
          <v-col
            v-for="benefit in benefits"
            :key="benefit.title"
            cols="12"
            md="6"
            class="mb-4"
          >
            <div class="d-flex align-start">
              <v-avatar size="60" color="white" class="mr-4">
                <v-icon :color="benefit.color" size="30">{{ benefit.icon }}</v-icon>
              </v-avatar>
              <div>
                <h3 class="headline font-weight-bold mb-2">
                  {{ benefit.title }}
                </h3>
                <p class="body-1">
                  {{ benefit.description }}
                </p>
              </div>
            </div>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- AI Features Section -->
    <section class="py-16">
      <v-container>
        <v-row align="center">
          <v-col cols="12" md="6">
            <h2 class="display-1 font-weight-bold mb-4">
              Inteligencia Artificial Integrada
            </h2>
            <p class="title font-weight-light mb-6">
              Potenciado por Claude AI para análisis inteligente de datos
            </p>
            
            <v-list class="transparent">
              <v-list-item
                v-for="aiFeature in aiFeatures"
                :key="aiFeature.title"
                class="px-0"
              >
                <v-list-item-avatar>
                  <v-icon :color="aiFeature.color">{{ aiFeature.icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title class="font-weight-bold">
                    {{ aiFeature.title }}
                  </v-list-item-title>
                  <v-list-item-subtitle>
                    {{ aiFeature.description }}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-col>
          
          <v-col cols="12" md="6" class="text-center">
            <v-card elevation="8" class="ai-demo-card">
              <v-card-text class="pa-8">
                <v-icon size="100" color="primary" class="mb-4">
                  mdi-brain
                </v-icon>
                <h3 class="headline font-weight-bold mb-4">
                  Claude AI Assistant
                </h3>
                <p class="body-1 mb-4">
                  Analiza tu inventario, optimiza precios y genera insights de negocio
                </p>
                <v-btn color="primary" large @click="goToAI">
                  <v-icon left>mdi-robot</v-icon>
                  Probar IA
                </v-btn>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- International Support -->
    <section class="grey lighten-5 py-16">
      <v-container>
        <v-row>
          <v-col cols="12" class="text-center mb-8">
            <h2 class="display-1 font-weight-bold mb-4">
              Soporte Internacional
            </h2>
            <p class="title font-weight-light">
              Adaptado para diferentes países y normativas fiscales
            </p>
          </v-col>
        </v-row>

        <v-row justify="center">
          <v-col
            v-for="country in supportedCountries"
            :key="country.code"
            cols="6"
            sm="4"
            md="2"
            class="text-center mb-4"
          >
            <v-card class="country-card h-100" hover elevation="2">
              <v-card-text class="pa-4">
                <div class="country-flag mb-2">
                  {{ country.flag }}
                </div>
                <h4 class="subtitle-2 font-weight-bold mb-1">
                  {{ country.name }}
                </h4>
                <v-chip
                  v-for="doc in country.documents"
                  :key="doc"
                  small
                  class="ma-1"
                  color="primary"
                  outlined
                >
                  {{ doc }}
                </v-chip>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Statistics -->
    <section class="primary darken-1 white--text py-16">
      <v-container>
        <v-row>
          <v-col
            v-for="stat in statistics"
            :key="stat.title"
            cols="6"
            md="3"
            class="text-center"
          >
            <h2 class="display-2 font-weight-bold mb-2">
              {{ stat.value }}
            </h2>
            <p class="title font-weight-light">
              {{ stat.title }}
            </p>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- CTA Section -->
    <section class="py-16">
      <v-container>
        <v-row justify="center">
          <v-col cols="12" md="8" class="text-center">
            <h2 class="display-1 font-weight-bold mb-4">
              ¿Listo para Transformar tu Negocio?
            </h2>
            <p class="title font-weight-light mb-8">
              Únete a cientos de empresas que ya confían en AI-POS para su gestión diaria
            </p>
            <v-btn
              x-large
              color="primary"
              class="ma-2"
              @click="startDemo"
            >
              <v-icon left>mdi-rocket-launch</v-icon>
              Comenzar Ahora
            </v-btn>
            <v-btn
              x-large
              outlined
              color="primary"
              class="ma-2"
              @click="contactSales"
            >
              <v-icon left>mdi-phone</v-icon>
              Contactar Ventas
            </v-btn>
          </v-col>
        </v-row>
      </v-container>
    </section>

    <!-- Footer -->
    <v-footer dark padless>
      <v-card flat tile width="100%" class="text-center">
        <v-card-text class="py-8">
          <v-btn
            v-for="social in socialLinks"
            :key="social.name"
            class="mx-4"
            dark
            icon
            @click="openLink(social.url)"
          >
            <v-icon size="24px">
              {{ social.icon }}
            </v-icon>
          </v-btn>
          
          <div class="mt-4">
            <p class="caption">
              © {{ currentYear }} AI-POS Sistema ERP. Desarrollado con Vue.js y Vuetify.
            </p>
            <p class="caption">
              Potenciado por Claude AI • Arquitectura moderna • Soporte internacional
            </p>
          </div>
        </v-card-text>
      </v-card>
    </v-footer>
  </div>
</template>

<script>
export default {
  name: 'HomePage',
  data() {
    return {
      currentYear: new Date().getFullYear(),
      
      mainFeatures: [
        {
          icon: 'mdi-receipt',
          title: 'Facturación Electrónica',
          description: 'Sistema completo de facturación electrónica con validación DIAN, AEAT y otros entes fiscales internacionales.',
          color: 'green',
          action: () => this.navigateTo('/InvoiceE')
        },
        {
          icon: 'mdi-store-outline',
          title: 'Multi-Tienda',
          description: 'Gestiona múltiples tiendas desde una sola plataforma con inventarios independientes y consolidados.',
          color: 'blue',
          action: () => this.navigateTo('/Company')
        },
        {
          icon: 'mdi-brain',
          title: 'Inteligencia Artificial',
          description: 'Claude AI integrado para análisis de datos, optimización de inventarios y insights de negocio.',
          color: 'purple',
          action: () => this.navigateTo('/aibos')
        }
      ],

      modules: [
        { name: 'Punto de Venta', icon: 'mdi-cash-register', color: 'green', description: 'POS completo con scanner' },
        { name: 'Inventario', icon: 'mdi-warehouse', color: 'blue', description: 'Control total de stock' },
        { name: 'Facturación', icon: 'mdi-receipt', color: 'orange', description: 'Facturas electrónicas' },
        { name: 'Caja', icon: 'mdi-cash', color: 'teal', description: 'Movimientos de efectivo' },
        { name: 'Clientes', icon: 'mdi-account-group', color: 'purple', description: 'CRM integrado' },
        { name: 'Compras', icon: 'mdi-truck', color: 'indigo', description: 'Gestión de proveedores' },
        { name: 'Reportes', icon: 'mdi-chart-line', color: 'red', description: 'Analytics avanzados' },
        { name: 'Configuración', icon: 'mdi-cog', color: 'grey', description: 'Ajustes del sistema' }
      ],

      technologies: [
        { name: 'Vue.js', icon: 'mdi-vuejs', color: 'green', description: 'Framework reactivo moderno' },
        { name: 'Vuetify', icon: 'mdi-material-design', color: 'blue', description: 'Material Design Components' },
        { name: 'PHP', icon: 'mdi-language-php', color: 'purple', description: 'Backend robusto y escalable' },
        { name: 'MySQL', icon: 'mdi-database', color: 'orange', description: 'Base de datos relacional' },
        { name: 'Claude AI', icon: 'mdi-robot', color: 'indigo', description: 'Inteligencia artificial' },
        { name: 'PWA', icon: 'mdi-cellphone-link', color: 'teal', description: 'Aplicación web progresiva' }
      ],

      benefits: [
        {
          icon: 'mdi-lightning-bolt',
          title: 'Alto Rendimiento',
          description: 'Arquitectura optimizada para procesar miles de transacciones por minuto con respuesta instantánea.',
          color: 'orange'
        },
        {
          icon: 'mdi-shield-check',
          title: 'Seguridad Avanzada',
          description: 'Autenticación multi-factor, cifrado de datos y cumplimiento de normativas internacionales.',
          color: 'green'
        },
        {
          icon: 'mdi-cloud-sync',
          title: 'Sincronización en Tiempo Real',
          description: 'Datos sincronizados instantáneamente entre todas las tiendas y dispositivos.',
          color: 'blue'
        },
        {
          icon: 'mdi-responsive',
          title: 'Diseño Responsive',
          description: 'Interfaz adaptativa que funciona perfectamente en desktop, tablet y móvil.',
          color: 'purple'
        }
      ],

      aiFeatures: [
        {
          icon: 'mdi-chart-line-variant',
          title: 'Análisis Predictivo',
          description: 'Predicción de demanda y optimización de stock',
          color: 'blue'
        },
        {
          icon: 'mdi-currency-usd',
          title: 'Optimización de Precios',
          description: 'Sugerencias inteligentes de precios basadas en mercado',
          color: 'green'
        },
        {
          icon: 'mdi-account-search',
          title: 'Segmentación de Clientes',
          description: 'Análisis automático del comportamiento de compra',
          color: 'purple'
        },
        {
          icon: 'mdi-file-document-outline',
          title: 'Procesamiento de Documentos',
          description: 'Extracción automática de datos de facturas y documentos',
          color: 'orange'
        }
      ],

      supportedCountries: [
        { name: 'España', flag: '🇪🇸', code: 'ES', documents: ['DNI', 'NIE', 'CIF'] },
        { name: 'Colombia', flag: '🇨🇴', code: 'CO', documents: ['CC', 'CE', 'NIT'] },
        { name: 'México', flag: '🇲🇽', code: 'MX', documents: ['CURP', 'RFC'] },
        { name: 'Argentina', flag: '🇦🇷', code: 'AR', documents: ['DNI', 'CUIL', 'CUIT'] },
        { name: 'Chile', flag: '🇨🇱', code: 'CL', documents: ['RUT', 'RUN'] },
        { name: 'Perú', flag: '🇵🇪', code: 'PE', documents: ['DNI', 'RUC'] }
      ],

      statistics: [
        { value: '95+', title: 'Endpoints API' },
        { value: '15+', title: 'Módulos Integrados' },
        { value: '6+', title: 'Países Soportados' },
        { value: '24/7', title: 'Disponibilidad' }
      ],

      socialLinks: [
        { name: 'GitHub', icon: 'mdi-github', url: '#' },
        { name: 'LinkedIn', icon: 'mdi-linkedin', url: '#' },
        { name: 'Twitter', icon: 'mdi-twitter', url: '#' },
        { name: 'YouTube', icon: 'mdi-youtube', url: '#' }
      ]
    }
  },

  methods: {
    scrollToFeatures() {
      this.$vuetify.goTo('#features', {
        duration: 800,
        offset: 0,
        easing: 'easeInOutCubic'
      })
    },

    navigateTo(route) {
      this.$router.push(route)
    },

    goToDemo() {
      this.$router.push('/PLU')
    },

    goToAI() {
      this.$router.push('/aibos')
    },

    startDemo() {
      this.$router.push('/Login')
    },

    contactSales() {
      window.open('mailto:ventas@ai-pos.com?subject=Consulta sobre AI-POS ERP', '_blank')
    },

    openLink(url) {
      if (url !== '#') {
        window.open(url, '_blank')
      }
    }
  }
}
</script>

<style scoped>
.hero-section {
  background: linear-gradient(45deg, #1976d2 30%, #42a5f5 90%);
}

.hero-content {
  height: 100%;
}

.feature-card {
  transition: all 0.3s ease;
  cursor: pointer;
}

.feature-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 8px 25px rgba(0,0,0,0.15) !important;
}

.module-card {
  transition: all 0.3s ease;
}

.module-card:hover {
  transform: translateY(-4px);
}

.tech-item {
  transition: all 0.3s ease;
  cursor: pointer;
}

.tech-item:hover {
  transform: scale(1.1);
}

.ai-demo-card {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
}

.country-card {
  transition: all 0.3s ease;
}

.country-card:hover {
  transform: translateY(-4px);
}

.country-flag {
  font-size: 3rem;
  line-height: 1;
}

.h-100 {
  height: 100%;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.v-card {
  animation: fadeInUp 0.6s ease-out;
}

/* Responsive adjustments */
@media (max-width: 600px) {
  .display-2 {
    font-size: 2rem !important;
  }
  
  .display-1 {
    font-size: 1.75rem !important;
  }
}
</style>
